import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchFilingsByTicker, fetchLatestCryptoSupplyByTickerAndAsset, fetchTickerProFormaTimeSeries, fetchTickerGrowthDriversTimeSeries, interpolateWeekendData, fetchSharesOutstandingSnapshot } from '../services/dataService';
import companiesData from '../data/companies.json';
import { useGlobalData } from '../context/GlobalDataContext';
import LineChart from './charts/LineChart';
import LineChartWithLegend from './charts/LineChartWithLegend';
import LineChartWithLegendZeroStart from './charts/LineChartWithLegendZeroStart';
import AreaChart from './charts/AreaChart';
import BarChart from './charts/BarChart';
import PremiumDiscountChart from './charts/PremiumDiscountChart';
import ChartModal from './ChartModal';
import ChartLogo from './ChartLogo';
import './CompanyDetail.css';

/**
 * Company Detail Page Component
 * Displays comprehensive financial data and charts for a selected company
 */
const CompanyDetail = ({ company, accentColor, assetType }) => {
  const [filings, setFilings] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [supplyData, setSupplyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalChart, setModalChart] = useState(null);
  const [isProFormaEnabled, setIsProFormaEnabled] = useState(false);
  const [currentFilingPage, setCurrentFilingPage] = useState(1);
  const [proFormaTimeSeriesData, setProFormaTimeSeriesData] = useState([]);
  const [growthDriversTimeSeriesData, setGrowthDriversTimeSeriesData] = useState([]);
  const [dailyCryptoHoldingsData, setDailyCryptoHoldingsData] = useState([]);
  const [chartStartDate, setChartStartDate] = useState(null);
  const interpolationInProgressRef = useRef(false); // Prevent concurrent interpolations
  const lastInterpolatedDataRef = useRef(null); // Track last interpolated data to avoid flickering
  const [sharesOutstandingData, setSharesOutstandingData] = useState(null);
  
  // Use global data context
  const { getTickerTimeSeries, getLatestDataByTicker, latestSnapshotData, latestTimeSeriesSnapshot, loadTimeSeriesData, loadFullTickerTimeSeries, loadLatestTimeSeriesSnapshot, allTimeSeriesData, loading: globalDataLoading, isTickerLoading } = useGlobalData();
  
  // Helper functions to get data from global context
  const timeSeriesData = useMemo(() => {
    if (!company?.ticker) return [];
    const data = getTickerTimeSeries(company.ticker);
    return Array.isArray(data) ? data : [];
  }, [company?.ticker, getTickerTimeSeries]);
  const latestDataMap = getLatestDataByTicker();
  const latestData = company?.ticker ? latestDataMap[company.ticker] : null;
  
  // Convert snapshot array to object keyed by ticker for easy lookup
  const snapshotData = useMemo(() => {
    if (!latestSnapshotData) return {};
    const indexed = {};
    latestSnapshotData.forEach(row => {
      indexed[row.ticker] = row;
    });
    return indexed;
  }, [latestSnapshotData]);
  
  // Get pro forma data for current company (lazy loads if needed)
  const proFormaData = useMemo(() => {
    // Trigger lazy load if data not loaded yet
    if (latestTimeSeriesSnapshot === null) {
      loadLatestTimeSeriesSnapshot();
      return null;
    }
    if (!company?.ticker) return null;
    return latestTimeSeriesSnapshot.find(row => row.ticker === company.ticker);
  }, [latestTimeSeriesSnapshot, company?.ticker, loadLatestTimeSeriesSnapshot]);
  
  // Determine which time series data to use based on pro forma toggle
  const activeTimeSeriesData = useMemo(() => {
    // Ensure proFormaTimeSeriesData is an array
    const safeProFormaData = Array.isArray(proFormaTimeSeriesData) ? proFormaTimeSeriesData : [];
    const safeTimeSeriesData = Array.isArray(timeSeriesData) ? timeSeriesData : [];
    
    // Only use proforma data if toggle is enabled, company supports it, AND we have data loaded
    if (isProFormaEnabled && company?.showProforma && safeProFormaData.length > 0) {
      // Map pro forma field names to regular field names for chart compatibility
      try {
        return safeProFormaData.map(d => ({
          ...d,
          mcap_estimated: d.mcap_estimated_proforma,
          nav_usd: d.nav_usd_proforma,
          premium_discount: d.premium_discount_proforma,
          outstanding_shares_estimated: d.outstanding_shares_estimated_proforma,
          mnav: d.mnav_proforma,
          nav_per_share: d.nav_per_share_proforma
        }));
      } catch (err) {
        return safeTimeSeriesData;
      }
    }
    // Fallback to regular time series data
    return safeTimeSeriesData;
  }, [isProFormaEnabled, company?.showProforma, proFormaTimeSeriesData, timeSeriesData]);
  
  const filteredTimeSeriesData = useMemo(() => {
    if (!chartStartDate) return timeSeriesData;
    const cutoff = new Date(chartStartDate);
    return timeSeriesData.filter(row => row?.date && new Date(row.date) >= cutoff);
  }, [timeSeriesData, chartStartDate]);

  const filteredActiveTimeSeriesData = useMemo(() => {
    if (!chartStartDate) return activeTimeSeriesData;
    const cutoff = new Date(chartStartDate);
    return activeTimeSeriesData.filter(row => row?.date && new Date(row.date) >= cutoff);
  }, [activeTimeSeriesData, chartStartDate]);

  // Helper function to get last non-null trading data (for weekends/mondays)
  const getLastNonNullTradingData = () => {
    if (!filteredTimeSeriesData || filteredTimeSeriesData.length === 0) return latestData;
    
    // Look for the most recent record with non-null trading data
    for (let i = filteredTimeSeriesData.length - 1; i >= 0; i--) {
      const record = filteredTimeSeriesData[i];
      if (record.stock_price_close !== null && 
          record.trading_volume_shares !== null && 
          record.trading_tx_count !== null) {
        return record;
      }
    }
    
    // Fallback to latest data if no non-null trading data found
    return latestData;
  };

  const lastTradingData = getLastNonNullTradingData();

  // Get latest non-null turnover value
  const latestTurnover = useMemo(() => {
    if (!filteredTimeSeriesData || filteredTimeSeriesData.length === 0) return lastTradingData?.turnover ?? null;
    
    // Look for the most recent record with non-null turnover
    for (let i = filteredTimeSeriesData.length - 1; i >= 0; i--) {
      const record = filteredTimeSeriesData[i];
      if (record.turnover !== null && record.turnover !== undefined) {
        return record.turnover;
      }
    }
    
    return lastTradingData?.turnover ?? null;
  }, [filteredTimeSeriesData, lastTradingData]);
  
  // Get dynamic ticker lists from companies.json
  const assetTickers = useMemo(() => {
    if (!companiesData) {
      return { bitcoin: [], ethereum: [], solana: [] };
    }
    const pick = (arr) => (arr || []).filter(x => x.trackData === true).map(x => x.ticker);
    return {
      bitcoin: pick(companiesData.bitcoin || []),
      ethereum: pick(companiesData.ethereum || []),
      solana: pick(companiesData.solana || [])
    };
  }, []);

  // Function to determine asset type based on ticker
  const getAssetType = useCallback((ticker) => {
    if (!ticker) return null;
    if (!assetTickers) return null;
    
    if (assetTickers.bitcoin && assetTickers.bitcoin.includes(ticker)) return 'BTC';
    if (assetTickers.ethereum && assetTickers.ethereum.includes(ticker)) return 'ETH';
    if (assetTickers.solana && assetTickers.solana.includes(ticker)) return 'SOL';
    return null;
  }, [assetTickers]);

  // Function to get asset-specific colors for premium/discount chart
  const getAssetColors = (ticker) => {
    // Use electric green for premium bars (above x-axis) and red shade for discount bars (below x-axis)
    return {
      premiumColor: '#00ff41', // Electric green for premium bars
      discountColor: '#dc2626'  // Red shade for discount bars
    };
  };

  // Function to get growth drivers legend labels based on asset type
  const getGrowthDriversLegendLabels = (ticker) => {
    const assetType = getAssetType(ticker);
    const assetName = assetType === 'BTC' ? 'BTC' : assetType === 'ETH' ? 'ETH' : assetType === 'SOL' ? 'SOL' : 'Crypto';
    
    // Order: Price at top, Per Share in middle, Nav Multiple at bottom
    return [
      `${assetName} Price`,       // g_crypto (top)
      `${assetName} per Share`,   // g_per_share (middle)
      'Nav Multiple'              // g_mnav (bottom)
    ];
  };

  // Function to get chart colors based on pro forma state
  const getChartColor = (ticker) => {
    if (!ticker) return '#10b981'; // Default green if no ticker
    
    const assetType = getAssetType(ticker);
    
    if (isProFormaEnabled) {
      // Light colors for pro forma view
      switch (assetType) {
        case 'BTC':
          return '#fde68a'; // Light gold
        case 'ETH':
          return '#93c5fd'; // Light blue
        case 'SOL':
          return '#c4b5fd'; // Light purple
        default:
          return '#86efac'; // Light green
      }
    } else {
      // Original vibrant colors
      switch (assetType) {
        case 'BTC':
          return '#fbbf24'; // Gold
        case 'ETH':
          return '#3b82f6'; // Blue
        case 'SOL':
          return '#8b5cf6'; // Purple
        default:
          return '#10b981'; // Green
      }
    }
  };

  // Trigger lazy loading of time series data when company changes (asset-specific)
  useEffect(() => {
    if (!company?.ticker || globalDataLoading) {
      // Clear growth drivers data if no ticker or still loading
      setGrowthDriversTimeSeriesData([]);
      return;
    }
    
    // Clear growth drivers data immediately when switching to a new ticker
    setGrowthDriversTimeSeriesData([]);
    
    // Determine asset name from assetType prop or from ticker
    let assetName = null;
    if (assetType === 'BTC') assetName = 'bitcoin';
    else if (assetType === 'ETH') assetName = 'ethereum';
    else if (assetType === 'SOL') assetName = 'solana';
    else {
      // Fallback: determine from ticker
      const tickerAssetType = getAssetType(company.ticker);
      if (tickerAssetType === 'BTC') assetName = 'bitcoin';
      else if (tickerAssetType === 'ETH') assetName = 'ethereum';
      else if (tickerAssetType === 'SOL') assetName = 'solana';
    }
    
    // Load lightweight time series data for this asset if not already loaded
    if (assetName && !allTimeSeriesData) {
      loadTimeSeriesData(assetName);
    }
    
    // Load full time series data for this specific ticker (for company detail page)
    // This will fetch all columns (nav_usd, premium_discount, trading_volume_shares, etc.)
    loadFullTickerTimeSeries(company.ticker);
    
    // Load proforma data in parallel if company supports it (regardless of toggle state)
    if (company?.showProforma) {
      fetchTickerProFormaTimeSeries(company.ticker)
        .then(data => {
          // Ensure data is always an array
          const safeData = Array.isArray(data) ? data : [];
          setProFormaTimeSeriesData(safeData);
        })
        .catch(err => {
          setProFormaTimeSeriesData([]);
        });
    } else {
      // Clear proforma data if company doesn't support it
      setProFormaTimeSeriesData([]);
      setIsProFormaEnabled(false); // Reset toggle when switching to non-proforma company
    }
    
    // Load growth drivers data in parallel (for all companies) - fetch fresh data for each ticker
    fetchTickerGrowthDriversTimeSeries(company.ticker)
      .then(data => {
        // Ensure data is always an array
        const safeData = Array.isArray(data) ? data : [];
        setGrowthDriversTimeSeriesData(safeData);
      })
      .catch(err => {
        setGrowthDriversTimeSeriesData([]);
      });
  }, [company?.ticker, company?.showProforma, assetType, allTimeSeriesData, globalDataLoading, loadTimeSeriesData, loadFullTickerTimeSeries, getAssetType]);

  useEffect(() => {
    // Don't start loading until global data is available or if no company
    if (globalDataLoading || !company?.ticker) {
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Determine asset type and fetch supply data
        let fetchAssetType = assetType;
        if (!fetchAssetType) {
          fetchAssetType = getAssetType(company.ticker);
        }
        
        // Use global data context instead of fetching directly
        const promises = [];
        
        // Add supply data fetch if we have a valid asset type
        if (fetchAssetType) {
          promises.push(fetchLatestCryptoSupplyByTickerAndAsset(company.ticker, fetchAssetType));
        }
        
        const results = await Promise.all(promises);
        const supply = results[0];
        
        // Only set supply data (time series and latest data come from global context)
        setSupplyData(supply || null);
      } catch (err) {
        setError(`Failed to load company data: ${err.message || 'Please try again.'}`);
      } finally {
        setLoading(false);
      }
    };

    const loadFilings = async () => {
      try {
        const filingsData = await fetchFilingsByTicker(company.ticker, 50); // Fetch up to 50 filings for pagination
        setFilings(filingsData || []);
        setCurrentFilingPage(1); // Reset to first page when loading new filings
      } catch (err) {
        // Don't set error state for filings - we don't want to block the whole page if filings fail
      }
    };

    loadData();
    loadFilings();
    // Snapshot data is now loaded from global context
  }, [company?.ticker, assetType, globalDataLoading, getAssetType]); // Only depend on ticker and assetType, not entire company object

  // Fetch shares outstanding data
  useEffect(() => {
    if (!company?.ticker) {
      setSharesOutstandingData(null);
      return;
    }

    const loadSharesOutstanding = async () => {
      try {
        const data = await fetchSharesOutstandingSnapshot(company.ticker);
        setSharesOutstandingData(data);
      } catch (err) {
        // Silently fail - don't block the page if shares outstanding data fails
        setSharesOutstandingData(null);
      }
    };

    loadSharesOutstanding();
  }, [company?.ticker]);

  // Format large numbers
  const formatNumber = (num, decimals = 2) => {
    if (!num && num !== 0) return 'N/A';
    
    if (Math.abs(num) >= 1e9) {
      return `$${(num / 1e9).toFixed(decimals)}B`;
    } else if (Math.abs(num) >= 1e6) {
      return `$${(num / 1e6).toFixed(decimals)}M`;
    } else if (Math.abs(num) >= 1e3) {
      return `$${(num / 1e3).toFixed(decimals)}K`;
    }
    return `$${num.toFixed(decimals)}`;
  };

  const formatNumberNoSymbol = (num, decimals = 2) => {
    if (!num && num !== 0) return 'N/A';
    
    if (Math.abs(num) >= 1e9) {
      return `${(num / 1e9).toFixed(decimals)}B`;
    } else if (Math.abs(num) >= 1e6) {
      return `${(num / 1e6).toFixed(decimals)}M`;
    } else if (Math.abs(num) >= 1e3) {
      return `${(num / 1e3).toFixed(decimals)}K`;
    }
    return num.toFixed(decimals);
  };

  const formatShares = (num) => {
    if (!num && num !== 0) return 'N/A';
    
    if (Math.abs(num) >= 1e9) {
      return `${(num / 1e9).toFixed(2)}B`;
    } else if (Math.abs(num) >= 1e6) {
      return `${(num / 1e6).toFixed(2)}M`;
    }
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateSupplyPercentage = () => {
    if (!supplyData?.pct_of_circulating) {
      return 'N/A';
    }
    
    // pct_of_circulating is already a percentage value
    const percentage = supplyData.pct_of_circulating;
    return percentage.toFixed(2) + '%';
  };

  // Prepare daily crypto holdings data (always use standard dataset)
  useEffect(() => {
    const baseData = Array.isArray(timeSeriesData) ? timeSeriesData : [];
    
    if (baseData.length === 0) {
      setChartStartDate(null);
      if (dailyCryptoHoldingsData.length > 0) {
        setDailyCryptoHoldingsData([]);
        lastInterpolatedDataRef.current = null;
      }
      return;
    }
    
    const mappedData = baseData.map(d => ({
      ...d,
      daily_crypto_holdings: d.crypto_holdings
    }));

    const firstValidEntry = mappedData.find(d => d.daily_crypto_holdings !== null && d.daily_crypto_holdings !== undefined);
    const startDate = firstValidEntry?.date || null;
    setChartStartDate(startDate);

    const filteredData = startDate
      ? mappedData.filter(d => {
          if (!d?.date) return false;
          return new Date(d.date) >= new Date(startDate);
        })
      : mappedData;

    const dataKey = filteredData.length > 0 
      ? `${filteredData.length}-${filteredData[0]?.date}-${filteredData[filteredData.length - 1]?.date}`
      : 'empty';

    if (lastInterpolatedDataRef.current === dataKey && dailyCryptoHoldingsData.length > 0) {
      return;
    }

    setDailyCryptoHoldingsData(filteredData);
    lastInterpolatedDataRef.current = dataKey;
  }, [timeSeriesData, dailyCryptoHoldingsData.length]);

  // Show loading state if global data is loading OR if we're waiting for time series data to load
  // OR if the specific ticker's full data is still loading
  const tickerStillLoading = company?.ticker ? isTickerLoading(company.ticker) : false;
  
  // Check if we have full data columns (nav_usd, premium_discount, trading_volume_shares)
  // These are the columns that only exist in full time series data
  const hasFullDataColumns = timeSeriesData && timeSeriesData.length > 0 && timeSeriesData.some(row => 
    row.hasOwnProperty('nav_usd') || 
    row.hasOwnProperty('premium_discount') || 
    row.hasOwnProperty('trading_volume_shares')
  );

  if (loading) {
    return (
      <div className="company-detail-empty">
        <p>Loading data for {company.name} ({company.ticker})...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-detail-error">
        <p>{error}</p>
      </div>
    );
  }

  // Show loading if:
  // 1. Global data is still loading
  // 2. No time series data exists yet
  // 3. Ticker is explicitly marked as loading
  // 4. We have time series data but it doesn't have full columns yet (still loading lightweight data)
  if (globalDataLoading || 
      (allTimeSeriesData === null) || 
      tickerStillLoading || 
      (timeSeriesData && timeSeriesData.length > 0 && !hasFullDataColumns)) {
    return (
      <div className="company-detail-empty">
        <p>Loading data for {company.name} ({company.ticker})...</p>
      </div>
    );
  }

  if (!timeSeriesData || timeSeriesData.length === 0) {
    return (
      <div className="company-detail-empty">
        <p>No data available for {company.name} yet.</p>
      </div>
    );
  }

  return (
    <div className="company-detail-page">
      {/* Summary Cards Section */}
      <div className="summary-cards">
        {/* Balance Sheet Card */}
        <div className="summary-card balance-sheet-card">
          <div className="summary-card-header">
            <h3>Balance Sheet</h3>
            <span className="summary-card-date">{formatDate(latestData?.series_date)}</span>
          </div>
          <div className="summary-card-content summary-grid-2col">
            <div className="summary-column">
              <div className="summary-row">
                <span className="summary-label">Current {company.name.includes('Ethereum') || company.ticker === 'ETHE' ? 'ETH' : company.name.includes('Bitcoin') || company.ticker === 'MSTR' ? 'BTC' : 'Crypto'} Holdings</span>
                <span className="summary-value-primary">
                  {latestData?.crypto_per_share && latestData?.outstanding_shares_estimated 
                    ? formatNumberNoSymbol(latestData.crypto_per_share * latestData.outstanding_shares_estimated)
                    : 'N/A'}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">% of Supply</span>
                <span className="summary-value">{calculateSupplyPercentage()}</span>
              </div>
            </div>
            <div className="summary-column">
              <div className="summary-row">
                <span className="summary-label">Total NAV</span>
                <span className="summary-value">{formatNumber(latestData?.nav_usd)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">mNAV</span>
                <span className="summary-value">{formatNumberNoSymbol(latestData?.mnav)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Market Metrics Card */}
        <div className="summary-card market-metrics-card">
          <div className="summary-card-header">
            <h3>Market Metrics</h3>
            <span className="summary-card-date">
              {lastTradingData?.date ? formatDate(lastTradingData.date) : 'Last 24h'}
            </span>
          </div>
          <div className="summary-card-content summary-grid-2col">
            <div className="summary-row">
              <span className="summary-label">Stock Price</span>
              <span className="summary-value">${lastTradingData?.stock_price_close?.toFixed(2) || 'N/A'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">NAV per Share</span>
              <span className="summary-value">${lastTradingData?.nav_per_share?.toFixed(2) || 'N/A'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Turnover</span>
              <span className="summary-value">
                {latestTurnover !== null
                  ? formatNumber(latestTurnover)
                  : 'N/A'}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Market Cap (Est.)</span>
              <span className="summary-value">
                {lastTradingData?.mcap_estimated 
                  ? formatNumber(lastTradingData.mcap_estimated)
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Shares Outstanding Card */}
        <div className="summary-card shares-card">
          <div className="summary-card-header">
            <h3>Shares Outstanding</h3>
            <div className="info-icon-wrapper">
              <svg className="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M8 12V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="8" cy="5" r="1" fill="currentColor"/>
              </svg>
              <span className="info-tooltip">
                For pre-funded warrants, the proceeds are received and counted towards NAV already, and hence its shares are counted in outstanding to reflect correct mNAV.
              </span>
            </div>
          </div>
          <div className="summary-card-content">
            <div className="summary-row">
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span className="summary-label">Latest SEC Filing</span>
                  <span className="summary-value">
                    {sharesOutstandingData?.sec_outstanding_shares
                      ? formatShares(sharesOutstandingData.sec_outstanding_shares)
                      : 'N/A'}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', color: '#888888' }}>
                    {sharesOutstandingData?.sec_filing_date
                      ? formatDate(sharesOutstandingData.sec_filing_date)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="summary-row">
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span className="summary-label">Estimated Shares Outstanding</span>
                  <span className="summary-value">
                    {sharesOutstandingData?.outstanding_shares_estimated 
                      ? formatShares(sharesOutstandingData.outstanding_shares_estimated)
                      : 'N/A'}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', color: '#888888' }}>
                    {sharesOutstandingData?.series_date
                      ? formatDate(sharesOutstandingData.series_date)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="summary-row">
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span className="summary-label">
                    <a 
                      href="https://massive.com" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ 
                        color: 'inherit', 
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px'
                      }}
                    >
                      Massive's
                    </a> Weighted Shares
                  </span>
                  <span className="summary-value">
                    {sharesOutstandingData?.weighted_shares_outstanding 
                      ? formatShares(sharesOutstandingData.weighted_shares_outstanding)
                      : 'N/A'}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', color: '#888888' }}>
                    {sharesOutstandingData?.polygon_query_date
                      ? formatDate(sharesOutstandingData.polygon_query_date)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dilutive Impact (Pro Forma) Card */}
        <div style={{ position: 'relative' }}>
          {!company?.showProforma && (
            <div style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              backgroundColor: assetType === 'BTC' ? '#f97316' : assetType === 'SOL' ? '#a855f7' : '#3b82f6',
              color: 'white',
              padding: '8px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              zIndex: 10,
              boxShadow: assetType === 'BTC' ? '0 4px 12px rgba(249, 115, 22, 0.4)' : assetType === 'SOL' ? '0 4px 12px rgba(168, 85, 247, 0.4)' : '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}>
              Coming Soon
            </div>
          )}
          <div className="summary-card proforma-card" style={{ opacity: company?.showProforma ? 1 : 0.5, pointerEvents: company?.showProforma ? 'auto' : 'none' }}>
          <div className="summary-card-header">
            <h3>Dilutive Impact <span style={{ fontSize: '12px', fontWeight: '500', opacity: 0.7, marginLeft: '8px' }}>(BETA)</span></h3>
            <div className="info-icon-wrapper">
              <svg className="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M8 12V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="8" cy="5" r="1" fill="currentColor"/>
              </svg>
              <span className="info-tooltip">
                For pro-forma accounting, only non pre-funded warrants, issued on and after PIPE investments are considered.
              </span>
            </div>
          </div>
          <div className="summary-card-content summary-grid-2col">
            <div className="summary-row">
              <span className="summary-label">PF NAV</span>
              <span className="summary-value">{company?.showProforma ? formatNumber(proFormaData?.nav_usd_proforma) : '-'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">PF FD Share Count</span>
              <span className="summary-value">{company?.showProforma ? formatNumberNoSymbol(proFormaData?.outstanding_shares_estimated_proforma) : '-'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">PF Premium or Discount</span>
              <span className="summary-value">{company?.showProforma ? (proFormaData?.premium_discount_proforma !== null && proFormaData?.premium_discount_proforma !== undefined ? `${(proFormaData.premium_discount_proforma * 100).toFixed(2)}%` : 'N/A') : '-'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">PF NAV per Share</span>
              <span className="summary-value">{company?.showProforma ? formatNumber(proFormaData?.nav_per_share_proforma) : '-'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">PF mNAV</span>
              <span className="summary-value">{company?.showProforma ? formatNumberNoSymbol(proFormaData?.mnav_proforma) : '-'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">PF Market Cap</span>
              <span className="summary-value">{company?.showProforma ? formatNumber(proFormaData?.mcap_estimated_proforma) : '-'}</span>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Recent Filings Table Section */}
      {filings && filings.length > 0 ? (
        <div className="filings-section">
          <div className="filings-table-container">
            <div className="filings-header">
              <h2 className="filings-title">Recent SEC Filings</h2>
              <span className="filings-cik">CIK: {filings[0]?.cik}</span>
            </div>
            <table className="filings-table">
              <thead>
                <tr>
                  <th>Filing Date</th>
                  <th>Form Type</th>
                  <th>Accession Number</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const itemsPerPage = 5;
                  const startIndex = (currentFilingPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedFilings = filings.slice(startIndex, endIndex);
                  
                  return paginatedFilings.map((filing, index) => (
                    <tr key={`${filing.accessionNumber}-${index}`}>
                      <td>{formatDate(filing.filingDate)}</td>
                      <td>
                        <span className="form-badge">{filing.form}</span>
                      </td>
                      <td className="accession-cell">{filing.accessionNumber}</td>
                      <td>
                        <a 
                          href={filing.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="filing-link"
                        >
                          View Filing →
                        </a>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
            <div className="filings-pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentFilingPage(prev => Math.max(1, prev - 1))}
                disabled={currentFilingPage === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentFilingPage} of {Math.ceil(filings.length / 5)}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentFilingPage(prev => Math.min(Math.ceil(filings.length / 5), prev + 1))}
                disabled={currentFilingPage >= Math.ceil(filings.length / 5)}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="filings-section">
          <p style={{ color: '#888', fontSize: '0.8rem' }}>
            Debug: No filings found for ticker {company.ticker}. Filings array: {JSON.stringify(filings)}
          </p>
        </div>
      )}

      {/* Featured Charts Section */}
      <div className="charts-header">
        <h2 className="charts-title">Featured Charts</h2>
      </div>
      <div className="charts-grid" style={{ marginBottom: '4rem' }}>
        {/* Growth Drivers Chart */}
        <div className="chart-container" onClick={() => !isMobile && setModalChart('growth_drivers')}>
          <div className="chart-logo-wrapper">
            <ChartLogo />
          </div>
          <LineChartWithLegend
            data={growthDriversTimeSeriesData}
            xKey="date"
            yKeys={['g_crypto', 'g_per_share', 'g_mnav']}
            title="Growth Drivers - Normalized"
            xLabel="Date"
            yLabel="Normalized Growth (x)"
            colors={['#ffff00', '#00ff41', '#3b82f6']}
            legendLabels={getGrowthDriversLegendLabels(company.ticker)}
            formatCurrency={false}
          />
        </div>

        {/* Price Comparison Chart */}
        <div className="chart-container" onClick={() => !isMobile && setModalChart('price_comparison')}>
          <div className="chart-logo-wrapper">
            <ChartLogo />
          </div>
          <LineChartWithLegendZeroStart
            data={growthDriversTimeSeriesData}
            xKey="date"
            yKeys={['fundamental_price', 'start_price', 'reconstructed_price']}
            title="Stock Price Decomposition"
            xLabel="Date"
            yLabel="Price"
            colors={[{ color: '#00ff41', fill: 'tozeroy', lineWidth: 0 }, { color: '#dc2626', dash: 'dash' }, '#ffff00']}
            legendLabels={['Fundamental Price', 'Start Price', 'Reconstructed Price']}
            formatCurrency={true}
          />
        </div>
      </div>

      {/* Charts Header with Pro Forma Toggle */}
      <div className="charts-header" style={{ marginTop: '2rem' }}>
        <h2 className="charts-title">Analytics</h2>
        {company?.showProforma && (
          <div className="proforma-toggle">
            <span className="toggle-label">
              Pro Forma View
              <span className="info-icon-wrapper">
                <svg className="info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <span className="info-tooltip">
                  {proFormaData ? 'Pro Forma view shows projected financial metrics assuming all convertible securities, warrants, and options are exercised, giving you a forward-looking diluted scenario.' : 'Coming Soon'}
                </span>
              </span>
            </span>
            <label className="toggle-switch" style={{ opacity: proFormaTimeSeriesData.length > 0 ? 1 : 0.5 }}>
              <input 
                type="checkbox" 
                checked={isProFormaEnabled}
                onChange={(e) => {
                  setIsProFormaEnabled(e.target.checked);
                }}
                disabled={proFormaTimeSeriesData.length === 0}
              />
              <span className="toggle-slider"></span>
            </label>
            {proFormaTimeSeriesData.length === 0 && (
              <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>Coming Soon</span>
            )}
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Analytics Charts Grid - 2x2 on desktop, 1x1 on smaller screens */}
        <div className="analytics-charts-grid">
          {/* Daily Total Crypto Holdings - Bar Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('total_crypto_holdings')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <BarChart
              data={dailyCryptoHoldingsData}
              xKey="date"
              yKey="daily_crypto_holdings"
              title={isProFormaEnabled && company?.showProforma ? "Daily Total Crypto Holdings (PF)" : "Daily Total Crypto Holdings"}
              xLabel="Date"
              yLabel={
                getAssetType(company.ticker) === 'BTC' ? 'BTC' 
                  : getAssetType(company.ticker) === 'ETH' ? 'ETH'
                  : getAssetType(company.ticker) === 'SOL' ? 'SOL'
                  : 'Crypto'
              }
              color={getChartColor(company.ticker)}
              opacity={0.6}
              formatCurrency={false}
            />
          </div>

          {/* Daily NAV (Crypto + Cash) - Area Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('nav')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <AreaChart
              data={filteredActiveTimeSeriesData}
              xKey="date"
              yKey="nav_usd"
              title={isProFormaEnabled && company?.showProforma ? "Daily NAV (Crypto + Cash) (PF)" : "Daily NAV (Crypto + Cash)"}
              xLabel="Date"
              yLabel="NAV"
              color={getChartColor(company.ticker)}
              formatCurrency={true}
            />
          </div>

          {/* Daily Outstanding Shares - Line Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('shares')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <LineChart
              data={filteredActiveTimeSeriesData}
              xKey="date"
              yKey="outstanding_shares_estimated"
              title={isProFormaEnabled && company?.showProforma ? "Daily Outstanding Shares (PF)" : "Daily Outstanding Shares"}
              xLabel="Date"
              yLabel="Shares"
              color={getChartColor(company.ticker)}
            />
          </div>

          {/* Daily Market Cap Over Time - Area Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('mcap')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <AreaChart
              data={filteredActiveTimeSeriesData}
              xKey="date"
              yKey="mcap_estimated"
              title={isProFormaEnabled && company?.showProforma ? "Daily Market Cap Over Time (PF)" : "Daily Market Cap Over Time"}
              xLabel="Date"
              yLabel="Market Cap"
              color={getChartColor(company.ticker)}
              formatCurrency={true}
            />
          </div>

          {/* Daily MNAV Over Time - Line Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('mnav')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <LineChart
              data={filteredActiveTimeSeriesData}
              xKey="date"
              yKey="mnav"
              title={isProFormaEnabled && company?.showProforma ? "Daily MNAV Over Time (PF)" : "Daily MNAV Over Time"}
              xLabel="Date"
              yLabel="MNAV Ratio"
              color={getChartColor(company.ticker)}
            />
          </div>

          {/* Daily Crypto Per Share - Bar Chart */}
          <div className="chart-container" onClick={() => !isMobile && setModalChart('crypto_per_share')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <BarChart
              data={filteredActiveTimeSeriesData}
              xKey="date"
              yKey="crypto_per_share"
              title={isProFormaEnabled && company?.showProforma ? "Daily Crypto Per Share (PF)" : "Daily Crypto Per Share"}
              xLabel="Date"
              yLabel={getAssetType(company.ticker) === 'BTC' ? 'BTC per Share' 
                : getAssetType(company.ticker) === 'ETH' ? 'ETH per Share'
                : getAssetType(company.ticker) === 'SOL' ? 'SOL per Share'
                : 'Crypto per Share'}
              color={getChartColor(company.ticker)}
              opacity={0.6}
            />
          </div>
        </div>

        {/* Daily Premium/Discount % - Premium/Discount Chart (Outside Grid) */}
        <div className="chart-container" onClick={() => !isMobile && setModalChart('premium_discount')}>
          <div className="chart-logo-wrapper">
            <ChartLogo />
          </div>
          <PremiumDiscountChart
            data={filteredActiveTimeSeriesData}
            xKey="date"
            title={isProFormaEnabled && company?.showProforma ? "Daily Premium/Discount % (PF)" : "Daily Premium/Discount %"}
            xLabel="Date"
            yLabel="Premium/Discount %"
            {...getAssetColors(company?.ticker)}
          />
        </div>
      </div>

      {/* Chart Modals */}
      <ChartModal isOpen={modalChart === 'total_crypto_holdings'} onClose={() => setModalChart(null)}>
        <BarChart
          data={dailyCryptoHoldingsData}
          xKey="date"
          yKey="daily_crypto_holdings"
          title={isProFormaEnabled && company?.showProforma ? "Daily Total Crypto Holdings (PF)" : "Daily Total Crypto Holdings"}
          xLabel="Date"
          yLabel={
            getAssetType(company.ticker) === 'BTC' ? 'BTC' 
              : getAssetType(company.ticker) === 'ETH' ? 'ETH'
              : getAssetType(company.ticker) === 'SOL' ? 'SOL'
              : 'Crypto'
          }
          color={getChartColor(company.ticker)}
          opacity={0.6}
          formatCurrency={false}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'crypto_holdings'} onClose={() => setModalChart(null)}>
        <BarChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="crypto_per_share"
          title={isProFormaEnabled && company?.showProforma ? "Daily Crypto Holdings Per Share (PF)" : "Daily Crypto Holdings Per Share"}
          xLabel="Date"
          yLabel="Crypto Holdings"
          color={getChartColor(company.ticker)}
          opacity={0.6}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'nav'} onClose={() => setModalChart(null)}>
        <AreaChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="nav_usd"
          title="Daily NAV (Crypto + Cash)"
          xLabel="Date"
          yLabel="NAV"
          color={getChartColor(company.ticker)}
          formatCurrency={true}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'shares'} onClose={() => setModalChart(null)}>
        <LineChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="outstanding_shares_estimated"
          title="Daily Outstanding Shares"
          xLabel="Date"
          yLabel="Shares"
          color={getChartColor(company.ticker)}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'mcap'} onClose={() => setModalChart(null)}>
        <AreaChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="mcap_estimated"
          title="Daily Market Cap Over Time"
          xLabel="Date"
          yLabel="Market Cap"
          color={getChartColor(company.ticker)}
          formatCurrency={true}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'mnav'} onClose={() => setModalChart(null)}>
        <LineChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="mnav"
          title="Daily MNAV Over Time"
          xLabel="Date"
          yLabel="MNAV Ratio"
          color={getChartColor(company.ticker)}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'crypto_per_share'} onClose={() => setModalChart(null)}>
        <BarChart
          data={filteredTimeSeriesData}
          xKey="date"
          yKey="crypto_per_share"
          title="Daily Crypto Per Share"
          xLabel="Date"
          yLabel={getAssetType(company.ticker) === 'BTC' ? 'BTC per Share' 
            : getAssetType(company.ticker) === 'ETH' ? 'ETH per Share'
            : getAssetType(company.ticker) === 'SOL' ? 'SOL per Share'
            : 'Crypto per Share'}
          color={getChartColor(company.ticker)}
          opacity={0.6}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'premium_discount'} onClose={() => setModalChart(null)}>
        <PremiumDiscountChart
          data={filteredTimeSeriesData}
          xKey="date"
          title="Daily Premium/Discount %"
          xLabel="Date"
          yLabel="Premium/Discount %"
          {...getAssetColors(company?.ticker)}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'growth_drivers'} onClose={() => setModalChart(null)}>
        <LineChartWithLegend
          data={growthDriversTimeSeriesData}
          xKey="date"
          yKeys={['g_crypto', 'g_per_share', 'g_mnav']}
          title="Growth Drivers"
          xLabel="Date"
          yLabel="Growth Rate"
          colors={['#ffff00', '#00ff41', '#3b82f6']}
          legendLabels={getGrowthDriversLegendLabels(company.ticker)}
          formatCurrency={false}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'new_chart_2'} onClose={() => setModalChart(null)}>
        <LineChart
          data={filteredActiveTimeSeriesData}
          xKey="date"
          yKey=""
          title=""
          xLabel="Date"
          yLabel=""
          color={getChartColor(company.ticker)}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'price_comparison'} onClose={() => setModalChart(null)}>
        <LineChartWithLegendZeroStart
          data={growthDriversTimeSeriesData}
          xKey="date"
          yKeys={['fundamental_price', 'start_price', 'reconstructed_price']}
          title="Stock Price Decomposition"
          xLabel="Date"
          yLabel="Price"
          colors={[{ color: '#00ff41', fill: 'tozeroy', lineWidth: 0 }, { color: '#dc2626', dash: 'dash' }, '#ffff00']}
          legendLabels={['Fundamental Price', 'Start Price', 'Reconstructed Price']}
          formatCurrency={true}
        />
      </ChartModal>
    </div>
  );
};

export default CompanyDetail;


